/**
 * Bunny.net CDN Service für CMS Frontend
 * Verwaltet API-Calls zu Bunny.net Storage und Stream
 */

import { getApiBaseUrl } from '../utils/apiConfig';
import { getAccessToken } from '../lib/authToken';
import { formatiereDateigroesse } from '../utils/dateigroesse';

const API_BASE_URL = getApiBaseUrl();

export interface BunnyFile {
  path: string;
  url: string;
  size: number;
  contentType: string;
}

export interface BunnyStorageStats {
  totalFiles: number;
  totalSize: number;
  folders: number;
}

export interface BunnyCDNResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

class BunnyCDNService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<BunnyCDNResponse<T>> {
    const token = await getAccessToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers: headers as HeadersInit,
      });

      // Prüfe, ob die Antwort JSON ist
      const contentType = response.headers.get('content-type');
      // Rohantwort des Backends: im Erfolgsfall { data: … }, im Fehlerfall { message | error | code }.
      // Manche Endpunkte liefern die Nutzlast direkt (deshalb unten `data.data || data`).
      let data: { data?: T; message?: string; error?: string; code?: string };

      if (contentType && contentType.includes('application/json')) {
        try {
          data = await response.json();
        } catch {
          // Wenn JSON-Parsing fehlschlägt, versuche Text zu lesen
          const text = await response.clone().text();
          return {
            success: false,
            message: text || `HTTP ${response.status}: ${response.statusText}`,
          };
        }
      } else {
        // Wenn keine JSON-Antwort, versuche Text zu lesen
        const text = await response.text();
        try {
          data = JSON.parse(text);
        } catch {
          return {
            success: false,
            message: text || `HTTP ${response.status}: ${response.statusText}`,
          };
        }
      }

      if (!response.ok) {
        // Spezielle Behandlung für 401 Unauthorized
        if (response.status === 401) {
          // Prüfe, ob es ein Bunny.net Authentifizierungsfehler ist
          const errorMessage = data.message || data.error || '';
          const errorCode = data.code || '';
          
          // Prüfe auf abgelaufenen Token
          if (errorMessage.includes('expired') || errorMessage.includes('jwt expired') || 
              errorCode === 'TOKEN_EXPIRED' || errorMessage.toLowerCase().includes('token expired')) {
            return {
              success: false,
              message: 'Ihr Login-Token ist abgelaufen. Bitte melden Sie sich erneut an.\n\n' +
                'Klicken Sie auf „Abmelden“ und danach auf „Anmelden“, um sich erneut anzumelden.',
            };
          }
          
          // Prüfe auf Bunny.net spezifische Fehler
          if (errorMessage.includes('Bunny.net') || errorMessage.includes('Storage Zone')) {
            return {
              success: false,
              message: `Bunny.net Authentifizierung fehlgeschlagen (401 Unauthorized).\n\n` +
                `Bitte überprüfen Sie die Backend-Konfiguration:\n` +
                `1. BUNNY_STORAGE_ZONE_PASSWORD ist in der Backend .env-Datei gesetzt\n` +
                `2. Das Storage Zone Password ist korrekt (nicht der API Key!)\n` +
                `3. Die Storage Zone existiert in Ihrem Bunny.net Account\n` +
                `4. Das Password hat die richtigen Berechtigungen\n\n` +
                `Fehler-Details: ${errorMessage}`,
            };
          }
          
          // Generischer Authentifizierungsfehler
          return {
            success: false,
            message: 'Authentifizierung erforderlich. Bitte melden Sie sich im Energetic Eternity System an.\n\n' +
              'Falls Sie bereits angemeldet sind, könnte Ihr Token abgelaufen sein. ' +
              'Bitte melden Sie sich erneut an.',
          };
        }
        
        // Spezielle Behandlung für 403 Forbidden
        if (response.status === 403) {
          if (data.code === 'ADMIN_REQUIRED') {
            return {
              success: false,
              message: 'Admin-Berechtigung erforderlich. Sie müssen ein Admin-Benutzer sein, um auf Bunny.net CDN zuzugreifen.',
            };
          }
          return {
            success: false,
            message: 'Zugriff verweigert. Admin-Berechtigung erforderlich.',
          };
        }
        
        // Spezielle Behandlung für Bunny.net Fehler
        const errorMessage = data.message || data.error || '';
        if (errorMessage.includes('Bunny.net') || errorMessage.includes('Storage Zone')) {
          return {
            success: false,
            message: errorMessage,
          };
        }
        
        return {
          success: false,
          message: data.message || data.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return {
        success: true,
        data: (data.data || data) as T,
      };
    } catch (error) {
      console.error('Bunny.net CDN Service Error:', error);
      console.error('Request URL:', `${this.baseUrl}${endpoint}`);
      console.error('Error Details:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
      
      // Spezifische Fehlermeldungen für verschiedene Fehlertypen
      if (error instanceof TypeError) {
        if (error.message.includes('fetch') || error.message.includes('Failed to fetch')) {
          return {
            success: false,
            message: `Verbindungsfehler: Backend-Server nicht erreichbar.\n\nBitte prüfen Sie:\n1. Läuft der Backend-Server auf Port 3000?\n2. Ist die API_BASE_URL korrekt? (Aktuell: ${this.baseUrl})\n3. Ist CORS korrekt konfiguriert?\n\nFehler: ${error.message}`,
          };
        }
        if (error.message.includes('network')) {
          return {
            success: false,
            message: `Netzwerkfehler: Keine Verbindung zum Backend-Server.\n\nBitte prüfen Sie:\n1. Ist der Backend-Server gestartet?\n2. Ist die Firewall korrekt konfiguriert?\n\nFehler: ${error.message}`,
          };
        }
      }
      
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unbekannter Fehler',
      };
    }
  }

  /**
   * Prüft, ob Bunny.net konfiguriert ist
   */
  async checkConfiguration(): Promise<BunnyCDNResponse<{ configured: boolean }>> {
    return this.request<{ configured: boolean }>('/api/admin/bunny/check');
  }

  /**
   * Listet Dateien in einem Ordner auf
   */
  async listFiles(folder: string = ''): Promise<BunnyCDNResponse<BunnyFile[]>> {
    const params = folder ? `?folder=${encodeURIComponent(folder)}` : '';
    return this.request<BunnyFile[]>(`/api/admin/bunny/files${params}`);
  }

  /**
   * Listet Ordner auf
   */
  async listFolders(folder: string = ''): Promise<BunnyCDNResponse<string[]>> {
    const params = folder ? `?folder=${encodeURIComponent(folder)}` : '';
    return this.request<string[]>(`/api/admin/bunny/folders${params}`);
  }

  /**
   * Ruft Informationen über eine Datei ab
   */
  async getFileInfo(filePath: string): Promise<BunnyCDNResponse<BunnyFile>> {
    const encodedPath = encodeURIComponent(filePath);
    return this.request<BunnyFile>(`/api/admin/bunny/file/${encodedPath}`);
  }

  // ⚠️ Hier stand `uploadFile(file, folder, fileName?)` — der Aufruf von
  // `POST /api/admin/bunny/upload`. Entfallen am 2026-09-08 (Tom-Entscheid: die CMS-Aufrufer
  // des Uploads gehen, der Ingest befuellt den Speicher). Die Mediathek bleibt vollstaendig
  // bedienbar — auflisten, durchsuchen, Ordner anlegen, auswaehlen, loeschen; nur das
  // Hinzufuegen von aussen faellt weg.
  // ⛔ Die Route SELBST steht noch. Sie abzuraeumen ist 🌐s Zug, und die Reihenfolge ist
  // Absicht: erst der Aufrufer (reversibel, sichtbar), dann die Route.

  /**
   * Löscht eine Datei
   */
  async deleteFile(filePath: string): Promise<BunnyCDNResponse<void>> {
    const encodedPath = encodeURIComponent(filePath);
    return this.request<void>(`/api/admin/bunny/file/${encodedPath}`, {
      method: 'DELETE',
    });
  }

  /**
   * Löscht mehrere Dateien in EINEM Aufruf.
   *
   * ⚠️ Vorher lief die Massenlöschung als Schleife über `deleteFile`: ein Klick auf 200 ausgewählte
   * Dateien erzeugte 202 Anfragen (200 Löschungen, dann Liste und Statistik) und sprengte den
   * globalen Begrenzer von 100 Anfragen je 15 Minuten. Ab Anfrage 101 kam 429 zurück, die zweite
   * Hälfte der Auswahl blieb liegen, und die Meldung nannte nur Zahlen — nicht, welche Datei noch
   * da war. Drosseln hätte das nicht behoben: das Fenster ist 15 Minuten lang.
   *
   * ⚠️ `results` trägt das Ergebnis JE PFAD. Die Oberfläche braucht das, um die fehlgeschlagenen
   * Dateien beim Namen zu nennen und ausgewählt zu lassen.
   */
  async deleteFiles(
    filePaths: string[]
  ): Promise<BunnyCDNResponse<{ results: { path: string; deleted: boolean }[]; deleted: number; failed: number }>> {
    return this.request<{ results: { path: string; deleted: boolean }[]; deleted: number; failed: number }>(
      '/api/admin/bunny/files/delete',
      {
        method: 'POST',
        body: JSON.stringify({ filePaths }),
      }
    );
  }

  /**
   * Erstellt einen Ordner
   */
  async createFolder(folderPath: string): Promise<BunnyCDNResponse<void>> {
    return this.request<void>('/api/admin/bunny/folder', {
      method: 'POST',
      body: JSON.stringify({ folderPath }),
    });
  }

  /**
   * Löscht einen Ordner
   */
  async deleteFolder(folderPath: string): Promise<BunnyCDNResponse<void>> {
    const encodedPath = encodeURIComponent(folderPath);
    return this.request<void>(`/api/admin/bunny/folder/${encodedPath}`, {
      method: 'DELETE',
    });
  }

  /**
   * Ruft Storage-Statistiken ab
   */
  async getStorageStats(): Promise<BunnyCDNResponse<BunnyStorageStats>> {
    return this.request<BunnyStorageStats>('/api/admin/bunny/stats');
  }

  /**
   * Formatiert Dateigröße
   */
  formatFileSize(bytes: number): string {
    // Die Rechnung wohnt seit der Fassungs-Übersicht in `utils/dateigroesse.ts` — sie hat dort
    // keine Importe und zieht deshalb keinen Supabase-Client in fremde Bäume (Begründung im Kopf
    // jener Datei). Diese Methode bleibt als Durchreiche stehen, damit die zwei Aufrufer in der
    // Mediathek und ihre Attrappe in `BunnyCDNManager.test.tsx` unverändert bleiben.
    return formatiereDateigroesse(bytes);
  }

  /**
   * Bestimmt Datei-Icon basierend auf Content-Type
   */
  getFileIcon(contentType: string): string {
    if (contentType.startsWith('audio/')) return '🎵';
    if (contentType.startsWith('video/')) return '🎬';
    if (contentType.startsWith('image/')) return '🖼️';
    if (contentType === 'application/pdf') return '📄';
    return '📁';
  }
}

export const bunnyCDNService = new BunnyCDNService();

